import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { StoryRow } from "../components/ListRows";

export default function NewsIndex() {
  const [news, setNews] = useState([]);
  const [params] = useSearchParams();
  const [game, setGame] = useState(params.get("game") || "all");
  useSeo({
    title: "News and Stories — KickStartKenyaFootball",
    description:
      "Kenyan football development news: stadiums, pitches, professional clubs and partnerships in the professional game, plus Harambee Stars fixtures and results in the national game.",
  });
  useEffect(() => {
    api.get(`/news${game === "all" ? "" : `?game=${encodeURIComponent(game)}`}`).then((r) => setNews(r.data));
  }, [game]);

  const [lead, ...rest] = news;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="news-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <p className="eyebrow">News and stories</p>
        <h1 className="display-1 mt-5">News And Stories</h1>
        <p className="body-copy mt-6 max-w-2xl">
          Stadiums, pitches, club investment, partnerships and Harambee Stars — split into the professional game
          (Kenya Premier League and Kenyans abroad) and the national game. For updates tied to a specific step, see{" "}
          <Link to="/updates" className="text-ochre underline decoration-maasai underline-offset-4">
            football development updates
          </Link>
          .
        </p>

        <div className="mt-10 flex flex-wrap gap-1" data-testid="news-game-tabs">
          {[
            ["all", "Everything"],
            ["Professional Game", "Professional game"],
            ["National Game", "National game"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setGame(key)}
              data-testid={`news-tab-${key.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-5 py-2.5 text-[0.64rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                game === key ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {!news.length ? (
          <p className="body-copy mt-12" data-testid="news-empty">
            No stories in this category yet.
          </p>
        ) : null}

        {lead && (
          <Reveal className="mt-14">
            <Link
              to={`/news/${lead.slug}`}
              data-testid={`news-lead-${lead.slug}`}
              className="group flex flex-col justify-between border border-white/10 bg-[#0b0b0b] p-8 transition-colors hover:border-maasai md:p-12"
            >
              <span className="text-[0.62rem] font-bold uppercase tracking-[0.22em] text-ochre">{lead.category}</span>
              <h2 className="display-2 mt-5 max-w-3xl transition-colors group-hover:text-ochre">{lead.title}</h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75">{lead.excerpt}</p>
              <div className="mt-8 flex items-center gap-3 text-[0.68rem] uppercase tracking-[0.16em] text-white/60">
                {lead.date}
                <ArrowRight size={15} className="text-ochre transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
            {(lead.sources || []).length ? (
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="news-lead-sources">
                <span className="text-[0.58rem] font-bold uppercase tracking-[0.2em] text-zinc-400">Sources</span>
                {lead.sources.map((s, i) => {
                  const src = typeof s === "string" ? { url: s, publisher: "Source" } : s;
                  return (
                    <a
                      key={src.url}
                      href={src.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`news-lead-source-${i}`}
                      className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400 hover:text-ochre"
                    >
                      {src.publisher || src.label} <ExternalLink size={11} />
                    </a>
                  );
                })}
              </div>
            ) : null}
          </Reveal>
        )}

        {rest.length ? (
          <div className="mt-14 border-t border-white/20" data-testid="news-list">
            {rest.map((n, i) => (
              <StoryRow key={n.id} story={n} index={i} />
            ))}
          </div>
        ) : (
          <p className="body-copy mt-14" data-testid="news-empty">
            {lead ? "That is the only story in this category so far." : "No stories in this category yet."}
          </p>
        )}
      </div>
    </div>
  );
}
